import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, Phone, BookOpen, GraduationCap } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface StudentProfileCardProps {
  student: {
    first_name: string;
    last_name: string;
    student_number: string;
    form_iv_index?: string | null;
    nactevet_reg_number?: string | null;
    email?: string | null;
    phone?: string | null;
    program?: string | null;
    nta_level?: string | null;
    status: string;
    sit_status?: string | null;
    admission_status?: string | null;
    gender?: string | null;
    date_of_birth?: string | null;
  };
  compact?: boolean;
}

const titleCase = (s: string) =>
  s.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

const fmt = (text: string) =>
  text.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const statusColor = (s: string) => {
  switch (s) {
    case "registered_active": case "active": return "default";
    case "unregistered_active": return "outline";
    case "unregistered_inactive": return "secondary";
    case "suspended": case "discontinued": case "terminated": case "deceased": return "destructive";
    default: return "secondary";
  }
};

export function StudentProfileCard({ student, compact = false }: StudentProfileCardProps) {
  const initials = `${student.first_name.charAt(0)}${student.last_name.charAt(0)}`.toUpperCase();

  if (compact) {
    return (
      <div className="flex items-center gap-3 rounded-lg border p-3 bg-card">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">{initials}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{student.first_name} {student.last_name}</p>
          <p className="text-xs text-muted-foreground">{student.program || "No program"}</p>
        </div>
        <Badge variant={statusColor(student.status) as any}>{fmt(student.status)}</Badge>
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        {/* Top section: avatar + name + badges */}
        <div className="flex flex-col items-center sm:flex-row sm:items-start gap-4">
          <Avatar className="h-16 w-16 md:h-20 md:w-20 shrink-0">
            <AvatarImage src="" alt={`${student.first_name} ${student.last_name}`} />
            <AvatarFallback className="bg-primary/10 text-primary font-bold text-xl md:text-2xl">
              {initials}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 text-center sm:text-left min-w-0">
            <h3 className="text-lg font-semibold leading-tight">
              {titleCase(`${student.first_name} ${student.last_name}`)}
            </h3>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-1.5">
              <Badge variant={statusColor(student.status) as any}>{fmt(student.status)}</Badge>
              {student.sit_status && (
                <Badge variant="outline">{fmt(student.sit_status)}</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Detail fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-3 pt-3 border-t">
          {student.nactevet_reg_number && (
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Registration No.</p>
                <p className="text-sm font-medium truncate">{student.nactevet_reg_number}</p>
              </div>
            </div>
          )}
          {student.program && (
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Program</p>
                <p className="text-sm font-medium truncate">{student.program}</p>
              </div>
            </div>
          )}
          {student.nta_level && (
            <div className="flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">NTA Level</p>
                <p className="text-sm font-medium">{student.nta_level}</p>
              </div>
            </div>
          )}
          {student.phone && (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Phone</p>
                <p className="text-sm font-medium">{student.phone}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
